import React, { useState, useMemo } from 'react'
import chainIconSrc from '../../assets/chain-icon.png'
import { getAPIBaseUrl } from '../../environment'
import './LoginPage.css'

/**
 * ModernizeIT Login Page with Flip Card
 *
 * Front: Agent Workflow Builder info with LOGIN button
 * Back: Glossy Dropbox-style login form
 */
const LoginPage = ({ onLoginSuccess }) => {
  const [isFlipped, setIsFlipped] = useState(false)
  const [isRegisterMode, setIsRegisterMode] = useState(false)
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false)
  const [isResetPasswordMode, setIsResetPasswordMode] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [remember, setRemember] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [message, setMessage] = useState('')

  // Live password validation (pure derived computation)
  const passwordChecks = useMemo(() => {
    const p = password ?? ""
    const c = confirmPassword ?? ""
    const checks = {
      len: p.length >= 8,
      upper: /[A-Z]/.test(p),
      lower: /[a-z]/.test(p),
      number: /[0-9]/.test(p),
      special: /[^A-Za-z0-9]/.test(p),
      match: c.length > 0 && p === c,
    }
    // Debug logging
    if (isRegisterMode) {
      console.log('[PasswordValidation] Checks:', checks, { password: p, confirmPassword: c })
    }
    return checks
  }, [password, confirmPassword, isRegisterMode])

  const canSubmitRegistration = useMemo(() => {
    const canSubmit = Object.values(passwordChecks).every(Boolean)
    if (isRegisterMode) {
      console.log('[PasswordValidation] Can submit registration:', canSubmit, passwordChecks)
    }
    return canSubmit
  }, [passwordChecks, isRegisterMode])

  const handleFlipToLogin = () => {
    setIsFlipped(true)
  }

  const handleBackToInfo = () => {
    setIsFlipped(false)
    setIsRegisterMode(false)
    setIsForgotPasswordMode(false)
    setIsResetPasswordMode(false)
    setMessage('')
  }

  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode)
    setIsForgotPasswordMode(false)
    setIsResetPasswordMode(false)
    // Clear fields when switching
    setName('')
    setEmail('')
    setOrganizationName('')
    setPassword('')
    setConfirmPassword('')
    setRemember(false)
    setMessage('')
  }

  const handleForgotPassword = (e) => {
    e.preventDefault()
    setIsForgotPasswordMode(true)
    setIsRegisterMode(false)
    setIsResetPasswordMode(false)
    setEmail('')
    setMessage('')
  }

  const handleBackToLogin = () => {
    setIsForgotPasswordMode(false)
    setIsResetPasswordMode(false)
    setIsRegisterMode(false)
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setResetToken('')
    setMessage('')
  }

  const validatePassword = (pwd) => {
    const minLength = pwd.length >= 8
    const hasUppercase = /[A-Z]/.test(pwd)
    const hasLowercase = /[a-z]/.test(pwd)
    const hasNumber = /\d/.test(pwd)
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)

    const errors = []
    if (!minLength) errors.push('at least 8 characters')
    if (!hasUppercase) errors.push('one uppercase letter')
    if (!hasLowercase) errors.push('one lowercase letter')
    if (!hasNumber) errors.push('one number')
    if (!hasSpecialChar) errors.push('one special character')

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    const API_URL = getAPIBaseUrl()

    try {
      // FORGOT PASSWORD MODE
      if (isForgotPasswordMode) {
        const response = await fetch(`${API_URL}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        })

        if (!response.ok) {
          const error = await response.json()
          setMessage(error.detail || 'Failed to send reset email')
          setLoading(false)
          return
        }

        const data = await response.json()
        setLoading(false)

        // For local testing: show the reset token
        if (data.reset_token) {
          setResetToken(data.reset_token)
          setIsResetPasswordMode(true)
          setIsForgotPasswordMode(false)
          setMessage(`Reset token (for testing): ${data.reset_token}`)
        } else {
          setMessage(data.message)
        }

        return
      }

      // RESET PASSWORD MODE
      if (isResetPasswordMode) {
        const passwordValidation = validatePassword(password)
        if (!passwordValidation.isValid) {
          setMessage(`Password must contain: ${passwordValidation.errors.join(', ')}`)
          setLoading(false)
          return
        }

        if (password !== confirmPassword) {
          setMessage('Passwords do not match!')
          setLoading(false)
          return
        }

        const response = await fetch(`${API_URL}/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: resetToken,
            password,
            confirmPassword
          })
        })

        if (!response.ok) {
          const error = await response.json()
          setMessage(error.detail || 'Failed to reset password')
          setLoading(false)
          return
        }

        const data = await response.json()
        setLoading(false)
        setMessage(data.message)

        // Wait 2 seconds then redirect to login
        setTimeout(() => {
          handleBackToLogin()
        }, 2000)

        return
      }

      // REGISTER MODE
      if (isRegisterMode) {
        // Defense-in-depth: block invalid submit even if button somehow enabled
        if (!canSubmitRegistration) {
          setMessage('Please fix all password requirements before creating account')
          setLoading(false)
          return
        }

        // Registration validation
        const passwordValidation = validatePassword(password)
        if (!passwordValidation.isValid) {
          alert(`Password must contain: ${passwordValidation.errors.join(', ')}`)
          setLoading(false)
          return
        }

        if (password !== confirmPassword) {
          alert('Passwords do not match!')
          setLoading(false)
          return
        }

        // Call registration API
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            email,
            organizationName,
            password,
            confirmPassword
          })
        })

        if (!response.ok) {
          const error = await response.json()
          alert(error.detail || 'Registration failed')
          setLoading(false)
          return
        }

        const data = await response.json()
        setLoading(false)

        // Store token and user data
        if (data.token) localStorage.setItem('modernizeit_token', data.token)
        localStorage.setItem('modernizeit_user', JSON.stringify(data.user))

        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess({
            token: data.token,
            user: data.user
          })
        }

      } else {
        // Call login API
        const response = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            remember
          })
        })

        if (!response.ok) {
          const error = await response.json()
          alert(error.detail || 'Invalid email or password')
          setLoading(false)
          return
        }

        const data = await response.json()
        setLoading(false)

        // Store token and user data
        if (data.token) localStorage.setItem('modernizeit_token', data.token)
        localStorage.setItem('modernizeit_user', JSON.stringify(data.user))

        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess({
            token: data.token,
            user: data.user
          })
        }
      }

    } catch (error) {
      console.error('Authentication error:', error)
      alert(`Authentication failed: ${error.message}`)
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      {/* Background */}
      <div className="login-background" />

      {/* Header */}
      <div className="login-header">
        <span className="login-brand">RecodeIT</span>
      </div>

      {/* Main Content Container */}
      <div className="login-container">
        {/* Flip Card Container */}
        <div className={`flip-card ${isFlipped ? 'flipped' : ''}`}>
          {/* FRONT SIDE - Agent Workflow Builder Info */}
          <div className="flip-card-front">
            {/* Chain Icon */}
            <div className="chain-icon">
              <img src={chainIconSrc} alt="Chain" />
            </div>

            {/* Title */}
            <h1 className="workflow-title">Mainframe Modernization Platform</h1>
            <p className="workflow-subtitle">
              Analyze, understand, and transform legacy applications into modern services
              <br />
              — combining automated analysis, intelligent transformation, and AI-driven insights.
            </p>

            {/* Platform Capabilities */}
            <div className="planned-features">
              <h3 className="features-header">Platform Capabilities</h3>
              <div className="features-grid">
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div>
                    <strong>Automated Analysis</strong>
                    <p>Deep code parsing, complexity metrics, and dependency mapping</p>
                  </div>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div>
                    <strong>Visual Pipeline</strong>
                    <p>Drag-and-drop workflow with real-time execution tracking</p>
                  </div>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div>
                    <strong>AI-Powered Insights</strong>
                    <p>Intelligent naming, documentation, and business logic extraction</p>
                  </div>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div>
                    <strong>Service Generation</strong>
                    <p>Transform legacy programs into modern microservices</p>
                  </div>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div>
                    <strong>Database Intelligence</strong>
                    <p>Data structure analysis, schema mapping, and entity modeling</p>
                  </div>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div>
                    <strong>Comprehensive Reports</strong>
                    <p>Technical specs, data dictionaries, and architecture reports</p>
                  </div>
                </div>
              </div>

              {/* Login Button INSIDE planned-features box */}
              <button className="login-flip-button" onClick={handleFlipToLogin}>
                LOGIN
              </button>
            </div>

            {/* Old "Implementation in progress..." removed — not appropriate for customer-facing */}
          </div>

          {/* BACK SIDE - Glossy Login Form */}
          <div className="flip-card-back">
            <div className="login-card">
              <div className="login-card-inner">
                {/* Lock Icon */}
                <div className="login-card-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2" />
                    <rect x="8" y="11" width="8" height="7" rx="1" />
                    <path d="M10 11V7a2 2 0 0 1 4 0v4" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>

                {/* Login/Register/Forgot Password Form */}
                <form onSubmit={handleSubmit} className="login-form">

                  {/* Message Display */}
                  {message && (
                    <div className="login-message" style={{
                      padding: '12px',
                      marginBottom: '16px',
                      borderRadius: '8px',
                      background: 'rgba(71, 201, 252, 0.1)',
                      border: '1px solid rgba(71, 201, 252, 0.3)',
                      color: '#47c9fc',
                      fontSize: '14px',
                      wordBreak: 'break-all'
                    }}>
                      {message}
                    </div>
                  )}

                  {/* FORGOT PASSWORD MODE - Email only */}
                  {isForgotPasswordMode && !isResetPasswordMode && (
                    <div className="login-field">
                      <div className="login-input-wrapper">
                        <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="M3 7l9 6 9-6" />
                        </svg>
                        <input
                          type="email"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="login-input"
                          required
                          autoFocus
                        />
                      </div>
                    </div>
                  )}

                  {/* RESET PASSWORD MODE - Token + New Password */}
                  {isResetPasswordMode && (
                    <>
                      <div className="login-field">
                        <div className="login-input-wrapper">
                          <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2L2 7l10 5 10-5-10-5z" />
                            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                          </svg>
                          <input
                            type="text"
                            placeholder="Reset Token"
                            value={resetToken}
                            onChange={(e) => setResetToken(e.target.value)}
                            className="login-input"
                            required
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="login-field">
                        <div className="login-input-wrapper">
                          <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="New Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="login-input"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="password-toggle"
                            aria-label="Toggle password visibility"
                          >
                            {showPassword ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="login-field">
                        <div className="login-input-wrapper">
                          <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="login-input"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="password-toggle"
                            aria-label="Toggle confirm password visibility"
                          >
                            {showConfirmPassword ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* REGISTER/LOGIN MODES */}
                  {!isForgotPasswordMode && !isResetPasswordMode && (
                    <>
                      {/* Name Field (Register only) */}
                      {isRegisterMode && (
                    <div className="login-field">
                      <div className="login-input-wrapper">
                        <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Full Name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="login-input"
                          required
                          autoFocus
                        />
                      </div>
                    </div>
                  )}

                  {/* Email Field (Register only) */}
                  {isRegisterMode && (
                    <div className="login-field">
                      <div className="login-input-wrapper">
                        <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="M3 7l9 6 9-6" />
                        </svg>
                        <input
                          type="email"
                          placeholder="Email Address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="login-input"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Organization Name Field (Register only) */}
                  {isRegisterMode && (
                    <div className="login-field">
                      <div className="login-input-wrapper">
                        <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Organization Name"
                          value={organizationName}
                          onChange={(e) => setOrganizationName(e.target.value)}
                          className="login-input"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Email Field (Login only) */}
                  {!isRegisterMode && (
                    <div className="login-field">
                      <div className="login-input-wrapper">
                        <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="M3 7l9 6 9-6" />
                        </svg>
                        <input
                          type="email"
                          placeholder="Email Address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="login-input"
                          required
                          autoFocus
                        />
                      </div>
                    </div>
                  )}

                  {/* Password Field */}
                  <div className="login-field password-field-wrapper">
                    <div className="login-input-wrapper">
                      <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="login-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="password-toggle"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {/* Password Requirements Tooltip (Register mode only) */}
                    {isRegisterMode && (
                      <div className="password-tooltip">
                        <div className="password-tooltip-title">Password must contain:</div>
                        <ul className="password-tooltip-list">
                          <li style={{ color: passwordChecks.len ? '#4ade80' : '#94a3b8' }}>
                            {passwordChecks.len ? '✅' : '❌'} At least 8 characters
                          </li>
                          <li style={{ color: passwordChecks.upper ? '#4ade80' : '#94a3b8' }}>
                            {passwordChecks.upper ? '✅' : '❌'} One uppercase letter (A-Z)
                          </li>
                          <li style={{ color: passwordChecks.lower ? '#4ade80' : '#94a3b8' }}>
                            {passwordChecks.lower ? '✅' : '❌'} One lowercase letter (a-z)
                          </li>
                          <li style={{ color: passwordChecks.number ? '#4ade80' : '#94a3b8' }}>
                            {passwordChecks.number ? '✅' : '❌'} One number (0-9)
                          </li>
                          <li style={{ color: passwordChecks.special ? '#4ade80' : '#94a3b8' }}>
                            {passwordChecks.special ? '✅' : '❌'} One special character (any non-letter/number)
                          </li>
                          <li style={{ color: passwordChecks.match ? '#4ade80' : '#94a3b8' }}>
                            {passwordChecks.match ? '✅' : '❌'} Passwords match
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Field (Register only) */}
                  {isRegisterMode && (
                    <div className="login-field">
                      <div className="login-input-wrapper">
                        <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm Password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="login-input"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="password-toggle"
                          aria-label="Toggle confirm password visibility"
                        >
                          {showConfirmPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Remember & Forgot Password (Login only) */}
                  {!isRegisterMode && !isForgotPasswordMode && !isResetPasswordMode && (
                    <div className="login-options">
                      <label className="login-remember">
                        <input
                          type="checkbox"
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                        />
                        <span>Remember</span>
                      </label>
                      <a href="#forgot" onClick={handleForgotPassword} className="login-forgot">
                        Forgot Password?
                      </a>
                    </div>
                  )}
                  </>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="login-button"
                    disabled={loading || (isRegisterMode && !canSubmitRegistration)}
                  >
                    {loading ? (
                      isForgotPasswordMode ? 'SENDING...' :
                      isResetPasswordMode ? 'RESETTING...' :
                      isRegisterMode ? 'CREATING ACCOUNT...' : 'LOGGING IN...'
                    ) : (
                      isForgotPasswordMode ? 'SEND RESET LINK' :
                      isResetPasswordMode ? 'RESET PASSWORD' :
                      isRegisterMode ? 'CREATE ACCOUNT' : 'LOGIN'
                    )}
                  </button>

                  {/* Toggle Login/Register/Back to Login */}
                  {!isForgotPasswordMode && !isResetPasswordMode && (
                    <div className="login-toggle">
                      <span className="login-toggle-text">
                        {isRegisterMode ? 'Already have an account?' : "Don't have an account?"}
                      </span>
                      <button type="button" className="login-toggle-link" onClick={toggleMode}>
                        {isRegisterMode ? 'Login' : 'Register'}
                      </button>
                    </div>
                  )}

                  {/* Back to Login Link (Forgot/Reset Password modes) */}
                  {(isForgotPasswordMode || isResetPasswordMode) && (
                    <div className="login-toggle">
                      <button type="button" className="login-toggle-link" onClick={handleBackToLogin} style={{
                        width: '100%',
                        textAlign: 'center'
                      }}>
                        ← Back to Login
                      </button>
                    </div>
                  )}
                </form>

                {/* Back Button */}
                <button className="back-button" onClick={handleBackToInfo}>
                  ← Back to Info
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
